import React, { useState, useEffect, useRef, FormEvent } from "react";

const subscribeForm = "https://dashboard.mailerlite.com/forms/1020808/126536398884832508/share";

// Function to handle email subscription
const findRequestURL = (mail: string): string => {
  const formURL = subscribeForm.split("/");
  const getNumbers = formURL.filter((item) => Number(item) && item);
  const accountID = getNumbers[0];
  const formID = getNumbers[1];
  const requestURL = `https://assets.mailerlite.com/jsonp/${accountID}/forms/${formID}/subscribe?fields[email]=${mail}`;
  return requestURL;
};

interface HeroProps {
  heading: string;
  message: string;
}

const Hero: React.FC<HeroProps> = ({ heading, message }) => {
  const [mail, setMail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [apiMessage, setApiMessage] = useState<string>("");

  // Handle email subscription
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    fetch(findRequestURL(mail), {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setApiMessage("Thanks for subscribing!");
        } else {
          setApiMessage("Something went wrong, please try again.");
        }
      })
      .catch(() => setApiMessage("Something went wrong, please try again."))
      .finally(() => {
        setLoading(false);
        setMail("");
      });
  };

  // Function to handle smooth scrolling to the pricing section (like Photobooth)
  const scrollToPricing = () => {
    const pricingSection = document.getElementById("pricing-section");
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: "smooth" });
    } else {
      // fallback to contact section if pricing-section not found
      const contactSection = document.getElementById("services");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Particle animation setup
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
    };
    resize();
    window.addEventListener('resize', resize);
    // Particle config (smaller, more depth)
    const PARTICLE_COUNT = 48;
    const particles = Array.from({ length: PARTICLE_COUNT }, () => {
      // Use a random scale for depth: smaller = further, larger = closer
      const scale = 0.5 + Math.random() * 0.7; // 0.5 to 1.2
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: (4 + Math.random() * 7) * scale, // radius 2-9px
        alpha: 0.07 + Math.random() * 0.10,
        dx: (Math.random() - 0.5) * 0.18 * scale * dpr, // smaller/further = slower
        dy: (Math.random() - 0.5) * 0.18 * scale * dpr,
      };
    });
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 2 * Math.PI);
        ctx.fillStyle = '#fff';
        ctx.shadowColor = '#fff';
        ctx.shadowBlur = 16;
        ctx.fill();
        ctx.restore();
        p.x += p.dx;
        p.y += p.dy;
        // Wrap around edges
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      }
      animationFrameId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen relative overflow-x-hidden">
      {/* Layer 1: Background Image */}
      <img
        src="/assets/hero-picnic-with-table-setting-sunflower-under-tree-shade-datesaverco-dmv-transformed.webp"
        alt="Background"
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      />
      {/* Layer 2: Black Tint Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-20 z-10" />
      {/* Layer 4: Particle Canvas Overlay */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full z-30 pointer-events-none"
        aria-hidden="true"
      />

      {/* Layer 5: Glassmorphism Blur Effect */}
      <div className="absolute inset-0 backdrop-blur-sm bg-white/10 z-20 opacity-50" />

      {/* Layer 6: Text Content */}
      <div className="flex flex-col items-center justify-center px-2 py-5 sm:p-5 text-center text-white relative z-40 max-w-full pt-28 sm:pt-32 overflow-x-hidden w-full">
        {/* Logo */}
        <div className="w-[95%] sm:max-w-[550px] md:max-w-[900px] mx-auto mb-6">
          <img
            src="/assets/cream-hero-logo.png"
            alt="Hero Logo"
            className="w-full h-auto object-contain"
          />
        </div>
        
        {/* Heading and Message */}
        <p
          className="font-the-seasons font-bold text-xl sm:text-xl md:text-3xl tracking-wide mb-2 bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient-move"
          style={{
            backgroundImage: 'linear-gradient(90deg, #f7ecd7 0%, #ffe59e 30%, #bfa77a 60%, #b6c48a 85%, #f7ecd7 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            backgroundSize: '200% 200%',
            animation: 'gradient-move 6s ease-in-out infinite',
          }}
        >
          Unplugged and Unfiltered
        </p>
        {/*
          Add animated gradient keyframes to global styles if not present:
          @keyframes gradient-move {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          Place this in your globals.css or index.css if not already present.
        */}
        <div className="font-columbia-serial text-sm sm:text-lg md:text-xl text-center mt-1">
          <div>Picnics<span className="mx-1 sm:mx-3" style={{ fontFamily: "Arial" }}>•</span> Event Content Creation <span className="mx-1 sm:mx-3" style={{ fontFamily: "Arial" }}>•</span> Inperson gatherings</div>
        </div>
        
        {/* Email Subscription Form */}
        <form onSubmit={onSubmit} className="mt-4 pt-12 sm:pt-24 relative flex flex-col items-center w-full">
        <div className="w-full flex flex-col items-center">
          {/* Input centered, button absolutely positioned to the right on desktop */}
          <div className="w-full md:w-[600px] mx-auto flex flex-col md:flex-row items-center md:items-stretch md:justify-center md:gap-5 relative">
            <input
              type="email"
              value={mail}
              onChange={(e) => setMail(e.target.value)}
              placeholder="Enter Your Email"
              className={`font-columbia-serial text-lg rounded-full px-0 py-1 text-white bg-white bg-opacity-15 placeholder-white text-center w-full md:w-auto flex-1 focus:shadow-[0_0_6px_1px_rgba(255,229,158,0.18)] focus:outline-none focus:ring-0 focus:scale-104 focus:border-2 focus:border-transparent focus:bg-gradient-to-r focus:from-[rgba(247,236,215,0.35)] focus:via-[rgba(255,229,158,0.22)] focus:to-[rgba(182,196,138,0.18)] focus:bg-clip-padding hover:shadow-[0_0_8px_2px_rgba(255,229,158,0.12)] hover:scale-102 transition-all duration-500 ease-in-out ${
                mail ? "bg-gray-100 bg-opacity-50" : ""
              }`}
              required
            />
            <button
              type="submit"
              className="hidden md:inline-block font-the-seasons px-5 py-2 min-w-[130px] bg-gradient-to-r from-[#231f20] to-[#393532] hover:from-[#393532] hover:to-[#231f20] text-[#f4f1e9] font-bold rounded-full border border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-base whitespace-nowrap md:ml-5 self-center md:self-auto"
              disabled={loading}
            >
              {loading ? "Loading..." : "Get Notified"}
            </button>
          </div>
        </div>
          {/* Displaying message below the Save My Date button */}
          {apiMessage && <p className="mt-2 text-[#ffde59] text-sm">{apiMessage}</p>}
        <p className="font-columbia-serial text-white text-xs sm:text-sm mb-4 px-2 italic pt-4 md:pt-5 md:whitespace-nowrap md:text-base">
          New experiences, exclusive offers and new ways to unplug straight to your inbox
        </p>
        {/* Button below tagline on mobile only */}
        <button
          type="submit"
          className="md:hidden font-the-seasons px-5 py-2 min-w-[110px] bg-gradient-to-r from-[#231f20] to-[#393532] hover:from-[#393532] hover:to-[#231f20] text-[#f4f1e9] font-bold rounded-full border border-[#231f20] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-base whitespace-nowrap"
          disabled={loading}
        >
          {loading ? "Loading..." : "Get Notified"}
        </button>
        </form>

        <button
          className="font-the-seasons px-8 py-3 bg-gradient-to-b from-white/20 via-white/5 to-white/0 text-white font-bold rounded-full border border-white/60 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 mt-28 md:mt-8 w-[60%] xs:w-[60%] sm:w-auto hover:bg-white hover:text-[#231f20] relative"
          style={{
            boxShadow: '0 4px 16px 0 rgba(31,38,135,0.12), 0 1.5px 0 0 rgba(255,255,255,0.18) inset',
          }}
          onClick={scrollToPricing}
        >
          <span className="drop-shadow-[0_2px_2px_rgba(0,0,0,0.10)]">BOOK NOW</span>
        </button>
      </div>
    </div>
  );
};

export default Hero;